import React from 'react';
import Debug from 'debug';
import slugify from 'slugify';
import LivefireCard from './LivefireCard';
import { generateCardId } from './feed';
import { defaultMenuConfig } from './menuItems';
import { metric, propagateMetric } from './util';
import { CreateLivefireCardCallbacks, LivefireProps, StatusActions } from './types';
import { MenuItem } from './LivefireCard/CardMenu';
import { LivefireCardProps } from './LivefireCard/LivefireCard';
import { FeedItem, MetroContextType } from '@/classes';
import { type NonUndefined } from '@/functions/typeHelpers';
import {
  isCommentFeedItem,
  isEmailFeedItem,
  isLikeFeedItem,
  isReplyWithVideoFeedItem,
  isVideoWatchedFeedItem,
} from '@/classes/LivefireApi/types';

// eslint-disable-next-line new-cap
const debug = Debug('livefire:organism:ui');

// eslint-disable-next-line import/prefer-default-export
export function createLivefireCard(
  metroContext: MetroContextType,
  callbacks: CreateLivefireCardCallbacks,
  menus: Partial<Record<FeedItem['type'], MenuItem[]>>,
  menuConfig: NonUndefined<LivefireProps['menuConfig']>,
  dispatch: React.Dispatch<StatusActions>,
  item: FeedItem,
  index: number,
) {
  const { onOpenUrlAction } = callbacks;
  const cardId = generateCardId(item);

  const menuItems = menus[item.type] ?? [];

  /** Properties that may exist on the FeedItem that need to be passed to the card */
  const conditionalProps: Partial<{
    commentMessage: LivefireCardProps['commentMessage'];
    replyThumbnail: LivefireCardProps['replyThumbnail'];
    replyVideoName: LivefireCardProps['replyVideoName'];
    replyVideoUrl: LivefireCardProps['replyVideoUrl'];
    count: LivefireCardProps['count'];
    percentWatched: LivefireCardProps['percentWatched'];
    maxVideoWatchedPercent: LivefireCardProps['maxVideoWatchedPercent'];
  }> = {};
  if (isCommentFeedItem(item)) {
    conditionalProps.commentMessage = item.commentMessage;
  } else if (isReplyWithVideoFeedItem(item)) {
    conditionalProps.replyThumbnail = item.replyThumbnail;
    conditionalProps.replyVideoName = item.replyVideoName;
    conditionalProps.replyVideoUrl = item.replyVideoUrl;
  } else if (isEmailFeedItem(item)) {
    conditionalProps.count = item.count;
  } else if (isVideoWatchedFeedItem(item)) {
    conditionalProps.percentWatched = item.percentWatched;
    conditionalProps.maxVideoWatchedPercent = item.maxVideoWatchedPercent;
    conditionalProps.count = item.count;
  } else if (isLikeFeedItem(item)) {
    conditionalProps.count = item.count;
  }

  return (
    <LivefireCard
      key={cardId}
      id={cardId}
      title={item.subject}
      subtitle={item.eventTS}
      sent={item.sentTS}
      recipient={item.recipientName}
      type={item.type}
      menuItems={menuItems.filter((mi) => !mi.alwaysVisible)}
      visibleMenuItems={menuItems.filter((mi) => mi.alwaysVisible)}
      onMenuItemClick={(selectedMenu) => {
        metric(
          `click.livefire.card_menu.${slugify(selectedMenu, {
            replacement: '_',
            lower: true,
          })}`,
          metroContext,
        )();
        if (menuConfig && menuConfig.onMenuItemClick?.(selectedMenu, item)) {
          return;
        }
        defaultMenuConfig.onMenuItemClick(selectedMenu, item);
      }}
      onMenuOpen={(isOpen) => {
        if (isOpen) {
          metric('click.livefire.card_menu_open', metroContext)();
        }
      }}
      onMetricTriggered={propagateMetric(metroContext)}
      onHelpLinkClick={(action) => {
        debug('help link clicked');
        if (typeof onOpenUrlAction === 'function') {
          action.event.preventDefault();
          onOpenUrlAction(action.url);
        }
      }}
      onHelpTooltipVisible={(tooltipStatus, cardPosition) => {
        // a tooltip is hovered, tell the state about it...
        dispatch({
          type: 'tooltip',
          payload: { visible: tooltipStatus, position: cardPosition },
        });
      }}
      position={index + 1}
      {...conditionalProps}
    />
  );
}
